import { useMemo, useRef, useState } from "react";
import { getImageUrl } from "@/lib/services/images";
import { Icon } from "@/components/icons/Icon";
import TerciaryButton from "@/components/buttons/terciary-button/TerciaryButton";

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function DownloadWithProgressButton({
  selectedIds,
}: {
  selectedIds: number[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [total, setTotal] = useState(0);
  const [done, setDone] = useState(0);
  const [currentName, setCurrentName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cancelRef = useRef(false);

  const percent = useMemo(() => {
    if (!total) return 0;
    return Math.round((done / total) * 100);
  }, [done, total]);

  const getFilenameFromUrl = (url: string) => {
    const parts = url.split("?")[0].split("/");
    return parts[parts.length - 1] || "unknown.jpg";
  };

  const handleCancel = () => {
    cancelRef.current = true;

    setIsOpen(false);
  };

  const handleOnDownload = async () => {
    if (!selectedIds.length) return;

    setError(null);
    setTotal(selectedIds.length);
    setDone(0);
    setCurrentName(null);
    setIsOpen(true);

    cancelRef.current = false;

    try {
      const urls = await Promise.all(selectedIds.map((id) => getImageUrl(id)));

      for (let i = 0; i < urls.length; i++) {
        if (cancelRef.current) break;

        const url = urls[i];
        const filenameClean = getFilenameFromUrl(url);

        setCurrentName(filenameClean);

        const link = document.createElement("a");
        link.href = url;
        link.download = filenameClean;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setDone(i + 1);
        await sleep(600);
      }

      setCurrentName(null);
      // si se quiere cerrar automáticamente al terminar:
      // setIsOpen(false);
    } catch (e: any) {
      setError(e?.message ?? "Error descargando imágenes");
    }
  };

  return (
    <>
      <button
        data-testid="DownloadWithProgressButton"
        type="button"
        className="flex items-center"
        onClick={handleOnDownload}
      >
        <Icon name="download" className="mr-1 inline-block size-4" />
        <span>Descargar</span>
      </button>

      {isOpen && (
        <div
          data-testid="DownloadWithProgressButtonDownload"
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 text-ib-gray-darkest"
        >
          <div className="w-[680px] max-w-[92vw] rounded bg-white shadow-[0_10px_30px_rgba(0,0,0,0.2)]">
            {/* Header */}
            <div className="flex items-start justify-between px-6 pt-6">
              <div>
                <h2 className="text-2xl font-semibold text-ib-gray-darkest">
                  Descargar fotos
                </h2>
                <p className="mt-2 max-w-[620px] text-sm text-ib-gray-main">
                  Debido al tamaño del archivo, el proceso puede tardar más de
                  lo esperado; una vez finalizado, la descarga se iniciará
                  automáticamente. Solo se descargará la página en la que te
                  encuentras actualmente.
                </p>
              </div>

              <button
                type="button"
                onClick={handleCancel}
                className="rounded p-1 text-ib-red hover:bg-ib-red/10"
                aria-label="Cerrar"
              >
                <Icon name="close" className="size-6 text-ib-red-dark" />
              </button>
            </div>

            {/* Progress card */}
            <div className="px-6 py-6">
              <div className="flex items-center gap-4 rounded border border-dashed border-ib-gray/30 p-4">
                <div className="flex size-9 items-center justify-center">
                  <Icon name="download" className="size-6" />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ib-gray-darkest">
                    {currentName ??
                      (percent === 100 ? "Completada" : "Preparando...")}
                  </p>
                  <div className="flex flex-col w-full gap-2">
                    <div className="mt-3">
                      <div className="h-[2px] w-full rounded-full bg-gray-200">
                        {/* barra progreso */}
                        <div
                          className="h-[4px] rounded-full bg-red-600 transition-[width] duration-200"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                    <div className=" flex justify-end">
                      <div className="flex shrink-0 items-center gap-2 text-sm text-ib-gray">
                        <span>
                          {percent === 100
                            ? "Descarga completada "
                            : "Descargando..."}
                        </span>
                        <span>{percent}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <p className="mt-3 text-sm text-ib-gray">
                  Descargadas <span className="font-semibold">{done}</span> de{" "}
                  <span className="font-semibold">{total}</span>
                </p>
              </div>

              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            </div>

            <div className="border-t border-ib-gray/20" />

            {/* Footer */}
            <div className="flex justify-center px-6 py-5">
              <TerciaryButton
                onClick={handleCancel}
                label={percent !== 100 ? "Cancelar" : "Cerrar"}
                colour={"red"}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
